import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useTranslation } from 'react-i18next';
import { notificationBody, notificationMeta, notificationTitle } from '@/lib/notificationContent';
import { useToast } from '@/context/ToastContext';
import {
  fetchNotifications,
  setAllNotificationsProcessed,
  setNotificationProcessed,
  simulateIncomingEvent,
  subscribe,
} from '@/services/notificationsService';
import type { NotificationView } from '@/types';

interface NotificationsContextValue {
  notifications: NotificationView[];
  loading: boolean;
  error: boolean;
  refetch: () => void;
  /** Subscribe to live arrivals so related parts can refresh. */
  subscribeEvents: (listener: (view: NotificationView) => void) => () => void;
  /** Preview-only sample event so the live behaviour can be exercised. */
  simulateIncoming: () => void;
  /** Flag or unflag a notification as handled in the log. */
  markProcessed: (id: string, processed: boolean) => void;
  /** Flag every notification in the log as handled at once. */
  markAllProcessed: () => void;
}

const NotificationsContext = createContext<NotificationsContextValue | null>(null);

export function NotificationsProvider({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const { pushToast } = useToast();
  const [notifications, setNotifications] = useState<NotificationView[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      setNotifications(await fetchNotifications());
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Live arrivals: brief non-blocking toast + append to the log. Related pages
  // subscribe separately through subscribeEvents.
  useEffect(() => {
    const unsubscribe = subscribe((view) => {
      setNotifications((prev) => [view, ...prev]);
      const meta = notificationMeta(view.kind);
      pushToast({
        title: notificationTitle(view, t),
        description: notificationBody(view, t),
        tone: 'info',
        icon: meta.icon,
        action: view.to ? { label: t('notifications.openOrder'), to: view.to } : null,
      });
    });
    return unsubscribe;
  }, [t, pushToast]);

  const subscribeEvents = useCallback(
    (listener: (view: NotificationView) => void) => subscribe(listener),
    [],
  );

  const simulateIncoming = useCallback(() => {
    simulateIncomingEvent();
  }, []);

  const markProcessed = useCallback((id: string, processed: boolean) => {
    const updated = setNotificationProcessed(id, processed);
    if (!updated) {
      return;
    }
    setNotifications((prev) => prev.map((item) => (item.id === id ? updated : item)));
  }, []);

  const markAllProcessed = useCallback(() => {
    setNotifications(setAllNotificationsProcessed());
  }, []);

  const value = useMemo<NotificationsContextValue>(
    () => ({
      notifications,
      loading,
      error,
      refetch: load,
      subscribeEvents,
      simulateIncoming,
      markProcessed,
      markAllProcessed,
    }),
    [
      notifications,
      loading,
      error,
      load,
      subscribeEvents,
      simulateIncoming,
      markProcessed,
      markAllProcessed,
    ],
  );

  return (
    <NotificationsContext.Provider value={value}>
      {children}
    </NotificationsContext.Provider>
  );
}

export function useNotifications(): NotificationsContextValue {
  const context = useContext(NotificationsContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationsProvider');
  }
  return context;
}